// Every status card is minted here, anchored after the newest row on screen (afterSeq): a later
// row passes it (splitPointForSeq) whatever the browser's clock says.
import { seqRangeOf } from "./history/rows.js";
import type { StreamEntry } from "./history/types.js";

export type StatusCard = Extract<StreamEntry, { kind: "error" }>;
type CardFacts = Omit<StatusCard, "id" | "kind" | "content" | "ts" | "seq" | "afterSeq">;

function newestOnScreen(entries: readonly StreamEntry[]): { seq?: number; ts?: number } {
  let seq: number | undefined;
  let ts: number | undefined;
  for (const e of entries) {
    const max = seqRangeOf(e)?.max;
    if (max === undefined) continue; // only a row's time is the server's
    seq = Math.max(seq ?? max, max);
    if (e.ts !== undefined) ts = Math.max(ts ?? e.ts, e.ts);
  }
  return { seq, ts };
}

export function statusCard(
  id: string,
  content: string,
  onScreen: readonly StreamEntry[],
  facts: CardFacts = {},
): StatusCard {
  const newest = newestOnScreen(onScreen);
  const ts = Math.max(Date.now(), (newest.ts ?? Number.NEGATIVE_INFINITY) + 1);
  const anchor = newest.seq === undefined ? {} : { afterSeq: newest.seq };
  return { id, kind: "error", content, ...facts, ...anchor, ts };
}

export function withoutAnchor(
  card: StatusCard,
  exact: { seq?: number; pendingFailureSeq?: number },
): StatusCard {
  if (exact.pendingFailureSeq !== undefined) return { ...card, ...exact };
  if (exact.seq === undefined) return card;
  const { afterSeq: _anchor, ...rest } = card;
  return { ...rest, seq: exact.seq };
}

export function pendingSeqOf(e: StreamEntry): number | undefined {
  return e.kind === "error" ? e.pendingFailureSeq : undefined;
}

export function isFailureCardFor(e: StreamEntry, seq: number): boolean {
  return e.kind === "error" && (e.seq === seq || e.pendingFailureSeq === seq);
}

// A stored row's card. A seqless row with its own time (attachRowTimestamps) is placed by that time
// alone: an anchor after the newest row on screen would seat an older page's card below newer rows.
export function rowCard(
  id: string,
  content: string,
  row: { seq?: number; ts?: number },
  onScreen: readonly StreamEntry[],
  facts: CardFacts = {},
): StatusCard {
  if (row.seq !== undefined) {
    return { id, kind: "error", content, ...facts, seq: row.seq, ts: row.ts ?? Date.now() };
  }
  if (row.ts === undefined) return statusCard(id, content, onScreen, facts);
  return { id, kind: "error", content, ...facts, ts: row.ts };
}

// A page row passes a card only when it is newer than the card's anchor; no anchor: by time.
export function rowPassesCard(card: StatusCard, seq: number, rowTs: number): boolean {
  return card.afterSeq === undefined
    ? card.ts !== undefined && card.ts <= rowTs
    : seq > card.afterSeq;
}

// A re-read keeps client cards whose place is in the page and that the page doesn't draw itself.
export function cardsKeptByPage(
  onScreen: readonly StreamEntry[],
  page: readonly StreamEntry[],
): StatusCard[] {
  const oldest = page
    .flatMap((e) => seqRangeOf(e)?.min ?? [])
    .reduce((a, b) => Math.min(a, b), Infinity);
  const oldestTs = page.flatMap((e) => e.ts ?? []).reduce((a, b) => Math.min(a, b), Infinity);
  return onScreen.filter((card): card is StatusCard => {
    if (card.kind !== "error" || card.seq !== undefined) return false;
    const byTime = card.afterSeq === undefined || oldest === Infinity;
    const inPage = byTime ? (card.ts ?? 0) >= oldestTs : (card.afterSeq ?? 0) >= oldest - 1;
    const drawn = page.some(
      (e) =>
        e.kind === "error" &&
        (card.pendingFailureSeq !== undefined
          ? e.seq === card.pendingFailureSeq
          : e.content.trim() === card.content.trim() &&
            (card.afterSeq === undefined || (e.seq ?? Infinity) > card.afterSeq)),
    );
    return inPage && !drawn;
  });
}

// A card drawn while no numbered row is on screen: the first committed row arrives after it.
export function anchorCards(
  entries: readonly StreamEntry[],
  seq: number,
): StreamEntry[] | undefined {
  if (entries.some((e) => seqRangeOf(e))) return undefined;
  const loose = (e: StreamEntry) =>
    e.kind === "error" && e.seq === undefined && e.afterSeq === undefined;
  if (!entries.some(loose)) return undefined;
  return entries.map((e) => (loose(e) ? { ...e, afterSeq: seq - 1 } : e));
}

// A card's anchor follows the numbered row seated above it (its prompt's row landing, a page).
export function reanchorCards(entries: readonly StreamEntry[]): StreamEntry[] | undefined {
  let above: number | undefined;
  let changed = false;
  const out = entries.map((e) => {
    const max = seqRangeOf(e)?.max;
    if (max !== undefined) above = max;
    if (e.kind !== "error" || e.seq !== undefined || above === undefined) return e;
    if ((e.afterSeq ?? -1) >= above) return e;
    changed = true;
    return { ...e, afterSeq: above };
  });
  return changed ? out : undefined;
}

function seatedAfter(e: StreamEntry | undefined, anchor: number): boolean {
  if (!e || seqRangeOf(e)) return false;
  return e.kind === "turn_boundary" || (e.kind === "error" && e.afterSeq === anchor);
}

// The numbered row a card seated at `at` by time stands after (or just before the first below it).
function timeAnchor(entries: readonly StreamEntry[], at: number): number | undefined {
  const above = entries.slice(0, at).findLast((e) => seqRangeOf(e));
  if (above) return seqRangeOf(above)?.max;
  const below = entries.slice(at).find((e) => seqRangeOf(e));
  return below ? (seqRangeOf(below)?.min ?? 1) - 1 : undefined;
}

export function seatCard(entries: readonly StreamEntry[], card: StatusCard): StreamEntry[] {
  const anchor = card.afterSeq;
  if (anchor === undefined) {
    const found = entries.findIndex((e) => e.ts !== undefined && e.ts > (card.ts ?? 0));
    const at = found < 0 ? entries.length : found;
    const afterSeq = timeAnchor(entries, at);
    const seated = afterSeq === undefined ? card : { ...card, afterSeq };
    return [...entries.slice(0, at), seated, ...entries.slice(at)];
  }
  let at = entries.findLastIndex((e) => (seqRangeOf(e)?.max ?? Infinity) <= anchor) + 1;
  while (seatedAfter(entries[at], anchor)) at += 1;
  return [...entries.slice(0, at), card, ...entries.slice(at)];
}
